import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Download,
  History,
  Pencil,
  LayoutList,
  Save,
  Boxes,
  ChevronRight,
  ChevronDown,
  PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import OverdueBorrowAlert from "../components/OverdueBorrowAlert";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStartStr = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const num = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function ProductList() {
  const { can } = useAuth();
  const write = can("warehouse_admin");
  const [warehouses, setWarehouses] = useState([]);
  const [bazarWarehouseIds, setBazarWarehouseIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all__");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [editP, setEditP] = useState(null);
  const [hist, setHist] = useState(null);
  const [hVar, setHVar] = useState("");
  const [hStart, setHStart] = useState(monthStartStr());
  const [hEnd, setHEnd] = useState(todayStr());
  useEffect(() => { setHVar(hist?.variant?.variant_id || ""); }, [hist]);
  const [expanded, setExpanded] = useState(new Set());

  const load = () => {
    api.get("/products/inventory", { params: q ? { q } : {} })
      .then((r) => {
        setWarehouses(r.data.warehouses || []);
        setBazarWarehouseIds(r.data.bazar_warehouse_ids || []);
        setProducts(r.data.products || []);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const reclassifyPO = async () => {
    if (!window.confirm("Rapikan stok produk PO?\n\nStok READY (Home/Store) untuk produk ber-flag \"Selalu simpan sebagai Stok PO\" akan dipindah ke bucket Pre Order. Aman & bisa dijalankan berulang.")) return;
    try {
      const r = await api.post("/products/reclassify-po-stock");
      const { moved_rows = 0, moved_qty = 0, message } = r.data || {};
      toast.success(moved_qty > 0 ? `${moved_qty} pcs (${moved_rows} baris) dipindah ke Pre Order` : (message || "Tidak ada stok PO yang perlu dipindah"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    const term = q.trim();
    if (term && term.length < 3) return; // auto-search mulai dari 3 huruf, tanpa Enter
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const categories = useMemo(() => Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort(), [products]);
  const filtered = useMemo(() => products.filter((p) => category === "__all__" || (p.category || "") === category), [products, category]);

  useEffect(() => { setPage(1); }, [category, q, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const fgWarehouses = useMemo(() => {
    const order = { HOME: 0, STORE: 1 };
    return warehouses
      .filter((w) => (w.category || "produk_jadi") === "produk_jadi")
      .sort((a, b) => {
        const oa = order[a.sales_bucket_tag] ?? 2, ob = order[b.sales_bucket_tag] ?? 2;
        return oa - ob || (a.name || "").localeCompare(b.name || "");
      });
  }, [warehouses]);
  const toggle = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const whStock = (p, wid) => (p.variants || []).reduce((s, v) => s + (v.per_warehouse?.[wid]?.available || 0), 0);
  const whKeep = (p, wid) => (p.variants || []).reduce((s, v) => s + (v.per_warehouse?.[wid]?.reserved || 0), 0);
  const bazarStock = (p) => bazarWarehouseIds.reduce((sum, warehouseId) => (
    sum + whStock(p, warehouseId)
  ), 0);
  const variantBazarStock = (variant) => bazarWarehouseIds.reduce((sum, warehouseId) => (
    sum + (variant.per_warehouse?.[warehouseId]?.available || 0)
  ), 0);
  const allStock = (product) => (product.variants || []).reduce((sum, variant) => (
    sum + (variant.available || 0)
  ), 0);

  const exportProductList = async () => {
    try {
      const response = await api.get("/products/export", {
        params: {
          q: q || undefined,
          category: category === "__all__" ? undefined : category,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `product-stock-${todayStr()}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Product list berhasil diekspor ke Excel");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="product-list-page">
      <OverdueBorrowAlert />
      <PageHeader
        title="Product List"
        subtitle="Stok Available seluruh gudang, termasuk total Bazar per produk"
      >
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk (auto, min 3 huruf)..." className="pl-9 bg-white w-full sm:w-56" data-testid="pl-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="pl-category-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Semua Kategori</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {can("product", "edit") && <Button variant="outline" onClick={reclassifyPO} className="border-cocoa text-cocoa" data-testid="pl-reclass-po-btn"><PackageCheck className="w-4 h-4 mr-1.5" /> Rapikan Stok PO</Button>}
        <Button variant="outline" onClick={exportProductList} className="border-taupe text-cocoa" data-testid="pl-export-btn"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
      </PageHeader>

      <div className="space-y-3 md:hidden" data-testid="pl-mobile-product-list">
        {pageItems.map((product) => (
          <div
            key={product.id}
            className="border border-taupe/25 rounded-lg bg-white p-3"
            data-testid={`pl-mobile-product-${product.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-cocoa">{product.name}</div>
                <div className="text-xs text-wmuted mt-1">
                  {product.category || "Tanpa kategori"} · {product.variant_count} varian
                </div>
              </div>
              <span className={product.active === false ? "text-wmuted text-xs" : "text-status-passed text-xs"}>
                {product.active === false ? "Nonaktif" : "Aktif"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div
                className="border border-taupe/15 rounded-md px-2 py-1.5"
                data-testid={`pl-mobile-all-${product.id}`}
              >
                <div className="text-wmuted truncate">All</div>
                <div className="font-semibold text-cocoa mt-0.5">
                  {num(allStock(product))}
                </div>
              </div>
              {fgWarehouses.map((warehouse) => {
                const stock = whStock(product, warehouse.id);
                const keep = whKeep(product, warehouse.id);
                return (
                  <div
                    key={warehouse.id}
                    className="border border-taupe/15 rounded-md px-2 py-1.5"
                    data-testid={`pl-mobile-wh-${product.id}-${warehouse.id}`}
                  >
                    <div className="text-wmuted truncate">{warehouse.name}</div>
                    <div className="font-semibold text-cocoa mt-0.5">
                      {num(stock)}
                      {keep > 0 && <span className="ml-1 text-amber-700">· Keep {num(keep)}</span>}
                    </div>
                  </div>
                );
              })}
              <div
                className="border border-taupe/15 rounded-md px-2 py-1.5"
                data-testid={`pl-mobile-preorder-${product.id}`}
              >
                <div className="text-wmuted truncate">Pre Order</div>
                <div className="font-semibold text-status-passed mt-0.5">
                  {num(product.preorder || 0)}
                </div>
              </div>
              <div
                className="border border-taupe/15 rounded-md px-2 py-1.5"
                data-testid={`pl-mobile-bazar-${product.id}`}
              >
                <div className="text-wmuted truncate">Bazar</div>
                <div className="font-semibold text-orange-700 mt-0.5">
                  {num(bazarStock(product))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <Button
                size="icon"
                variant="ghost"
                title="Riwayat stok"
                onClick={() => setHist({ product })}
                data-testid={`pl-mobile-history-${product.id}`}
              >
                <History className="w-4 h-4 text-taupe" />
              </Button>
              {write && (
                <Button
                  size="icon"
                  variant="ghost"
                  title="Edit stok per gudang"
                  onClick={() => setDetail({ product, initialTab: "gudang" })}
                  data-testid={`pl-mobile-edit-stock-${product.id}`}
                >
                  <Boxes className="w-4 h-4 text-status-passed" />
                </Button>
              )}
              {write && (
                <Button
                  size="icon"
                  variant="ghost"
                  title="Edit produk"
                  onClick={() => setEditP(product)}
                  data-testid={`pl-mobile-edit-${product.id}`}
                >
                  <Pencil className="w-4 h-4 text-cocoa" />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                title="Detail inventory"
                onClick={() => setDetail({ product, initialTab: "varian" })}
                data-testid={`pl-mobile-detail-${product.id}`}
              >
                <LayoutList className="w-4 h-4 text-cocoa" />
              </Button>
            </div>
          </div>
        ))}
        {!pageItems.length && (
          <div className="text-center text-wmuted py-10" data-testid="pl-mobile-empty-state">
            Belum ada produk. Lakukan Sinkronisasi POS terlebih dahulu.
          </div>
        )}
      </div>

      <Panel className="hidden md:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8"></TableHead>
                <TableHead className="w-12">No</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right whitespace-nowrap">All</TableHead>
                {fgWarehouses.map((w) => (
                  <TableHead key={w.id} className="text-right whitespace-nowrap">
                    <div className="flex flex-col items-end leading-tight">
                      <span>{w.name}</span>
                      {w.sales_bucket_tag && <span className={`text-[10px] font-semibold ${w.sales_bucket_tag === "HOME" ? "text-emerald-600" : "text-indigo-600"}`} data-testid={`pl-whtag-${w.id}`}>= {w.sales_bucket_tag === "HOME" ? "Home" : "Store"}</span>}
                    </div>
                  </TableHead>
                ))}
                <TableHead className="text-right whitespace-nowrap text-status-passed">Pre Order</TableHead>
                <TableHead className="text-right whitespace-nowrap text-orange-700">
                  Bazar
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((p, idx) => {
                const isOpen = expanded.has(p.id);
                return (
                  <React.Fragment key={p.id}>
                    <TableRow data-testid={`pl-product-${p.id}`}>
                      <TableCell>
                        {(p.variants || []).length > 0 && (
                          <button onClick={() => toggle(p.id)} className="text-taupe hover:text-cocoa" data-testid={`pl-expand-${p.id}`}>
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        )}
                      </TableCell>
                      <TableCell>{(page - 1) * pageSize + idx + 1}</TableCell>
                      <TableCell className="font-semibold text-cocoa">{p.name}<span className="text-wmuted font-normal text-xs ml-1">({p.variant_count} varian)</span></TableCell>
                      <TableCell>{p.category || "-"}</TableCell>
                      <TableCell className="text-right" data-testid={`pl-all-${p.id}`}>
                        <span className={allStock(p) > 0 ? "font-semibold text-cocoa" : "text-wmuted"}>
                          {num(allStock(p))}
                        </span>
                      </TableCell>
                      {fgWarehouses.map((w) => {
                        const st = whStock(p, w.id), kp = whKeep(p, w.id);
                        return (
                          <TableCell key={w.id} className="text-right" data-testid={`pl-wh-${p.id}-${w.id}`}>
                            <span className={`font-semibold ${st > 0 ? "text-cocoa" : "text-wmuted"}`}>{num(st)}</span>
                            {kp > 0 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid={`pl-wh-keep-${p.id}-${w.id}`}>{num(kp)}</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right" data-testid={`pl-preorder-${p.id}`}>
                        <span className={`font-semibold ${(p.preorder || 0) > 0 ? "text-status-passed" : "text-wmuted"}`}>{num(p.preorder || 0)}</span>
                      </TableCell>
                      <TableCell
                        className="text-right"
                        data-testid={`pl-bazar-${p.id}`}
                      >
                        <span
                          className={`font-semibold ${
                            bazarStock(p) > 0 ? "text-orange-700" : "text-wmuted"
                          }`}
                        >
                          {num(bazarStock(p))}
                        </span>
                      </TableCell>
                      <TableCell><span className={p.active === false ? "text-wmuted" : "text-status-passed"}>{p.active === false ? "Nonaktif" : "Aktif"}</span></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" title="Riwayat stok" onClick={() => setHist({ product: p })} data-testid={`pl-history-${p.id}`}><History className="w-4 h-4 text-taupe" /></Button>
                        {write && (
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Edit stok per gudang"
                            onClick={() => setDetail({ product: p, initialTab: "gudang" })}
                            data-testid={`pl-edit-stock-${p.id}`}
                          >
                            <Boxes className="w-4 h-4 text-status-passed" />
                          </Button>
                        )}
                        {write && (
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Edit produk"
                            onClick={() => setEditP(p)}
                            data-testid={`pl-edit-${p.id}`}
                          >
                            <Pencil className="w-4 h-4 text-cocoa" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Detail inventory"
                          onClick={() => setDetail({ product: p, initialTab: "varian" })}
                          data-testid={`pl-detail-${p.id}`}
                        >
                          <LayoutList className="w-4 h-4 text-cocoa" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isOpen && (p.variants || []).length > 0 && (
                      <TableRow className="bg-taupe/10 hover:bg-taupe/10" data-testid={`pl-subhead-${p.id}`}>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-[11px] font-semibold text-wmuted uppercase tracking-wide">Color Size</TableCell>
                        <TableCell className="text-[11px] font-semibold text-wmuted uppercase tracking-wide">Selling Price</TableCell>
                        <TableCell className="text-right text-[11px] font-semibold text-wmuted">All</TableCell>
                        {fgWarehouses.map((w) => (
                          <TableCell key={w.id} className="text-right text-[11px] font-semibold text-wmuted">
                            {w.name}{w.sales_bucket_tag && <span className={`ml-1 ${w.sales_bucket_tag === "HOME" ? "text-emerald-600" : "text-indigo-600"}`}>= {w.sales_bucket_tag === "HOME" ? "Home" : "Store"}</span>}
                          </TableCell>
                        ))}
                        <TableCell className="text-right text-[11px] font-semibold text-status-passed">Pre Order</TableCell>
                        <TableCell className="text-right text-[11px] font-semibold text-orange-700">
                          Bazar
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    )}
                    {isOpen && (p.variants || []).map((v) => (
                      <TableRow key={v.variant_id} className="bg-taupe/5" data-testid={`pl-varrow-${v.variant_id}`}>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell className="pl-6 text-sm text-cocoa">{v.color}/{v.size}</TableCell>
                        <TableCell className="text-sm" data-testid={`pl-varprice-${v.variant_id}`}>{v.selling_price ? fmtIDR(v.selling_price) : "-"}</TableCell>
                        <TableCell className="text-right text-sm" data-testid={`pl-var-all-${v.variant_id}`}>
                          <span className={v.available > 0 ? "text-cocoa" : "text-wmuted"}>
                            {num(v.available)}
                          </span>
                        </TableCell>
                        {fgWarehouses.map((w) => {
                          const st = v.per_warehouse?.[w.id]?.available || 0, kp = v.per_warehouse?.[w.id]?.reserved || 0;
                          return (
                            <TableCell key={w.id} className="text-right text-sm">
                              <span className={st > 0 ? "text-cocoa" : "text-wmuted"}>{num(st)}</span>
                              {kp > 0 && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{num(kp)}</span>}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right text-sm" data-testid={`pl-var-preorder-${v.variant_id}`}>
                          <span className={(v.preorder || 0) > 0 ? "text-status-passed font-medium" : "text-wmuted"}>{num(v.preorder || 0)}</span>
                        </TableCell>
                        <TableCell
                          className="text-right text-sm"
                          data-testid={`pl-var-bazar-${v.variant_id}`}
                        >
                          <span
                            className={
                              variantBazarStock(v) > 0 ? "text-orange-700" : "text-wmuted"
                            }
                          >
                            {num(variantBazarStock(v))}
                          </span>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right"><Button size="sm" variant="ghost" title="Riwayat stok varian" onClick={() => setHist({ product: p, variant: v })} className="text-taupe h-7 px-2" data-testid={`pl-varhistory-${v.variant_id}`}><History className="w-3.5 h-3.5" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {!pageItems.length && <TableRow><TableCell colSpan={fgWarehouses.length + 9} className="text-center text-wmuted py-10">Belum ada produk. Lakukan Sinkronisasi POS terlebih dahulu.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <span>Show</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-20 bg-white h-8" data-testid="pl-pagesize"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-wmuted">dari {filtered.length} produk</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa" data-testid="pl-prev">Previous</Button>
          <span className="px-2">Hal {page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa" data-testid="pl-next">Next</Button>
        </div>
      </div>

      {detail && (
        <DetailDialog
          product={detail.product}
          warehouses={warehouses}
          write={write}
          initialTab={detail.initialTab}
          onClose={() => setDetail(null)}
          onSaved={load}
          onHistory={(variant) => setHist({ product: detail.product, variant })}
        />
      )}
      {editP && <EditProductDialog product={editP} categories={categories} onClose={() => setEditP(null)} onSaved={() => { setEditP(null); load(); }} />}

      {hist && (
        <Dialog open onOpenChange={() => setHist(null)}>
          <DialogContent data-testid="pl-history-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-cocoa">
                Riwayat Stok — {hist.product?.name}
              </DialogTitle>
              <DialogDescription className="sr-only" data-testid="pl-history-description">
                Pilih varian dan rentang tanggal untuk melihat riwayat stok.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Varian</Label>
                <Select value={hVar || undefined} onValueChange={setHVar}>
                  <SelectTrigger data-testid="pl-hist-variant"><SelectValue placeholder="Pilih varian" /></SelectTrigger>
                  <SelectContent>{(hist.product?.variants || []).map((v) => <SelectItem key={v.variant_id} value={v.variant_id}>{v.color}/{v.size}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={hStart} onChange={(e) => setHStart(e.target.value)} data-testid="pl-hist-start" /></div>
                <div><Label>End Date</Label><Input type="date" value={hEnd} onChange={(e) => setHEnd(e.target.value)} data-testid="pl-hist-end" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHist(null)} className="border-taupe text-cocoa">Cancel</Button>
              <Button disabled={!hVar} onClick={() => { window.open(`/stock-history/${hVar}?start=${hStart}&end=${hEnd}`, "_blank"); setHist(null); }} data-testid="pl-hist-show" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Show History</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DetailDialog({
  product,
  warehouses,
  write,
  initialTab = "varian",
  onClose,
  onHistory,
  onSaved,
}) {
  const vs = product.variants || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="pl-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">{product.name}</DialogTitle>
          <DialogDescription className="sr-only" data-testid="pl-detail-description">
            Detail stok produk per varian dan gudang.
          </DialogDescription>
          <div className="flex flex-wrap gap-2 pt-1 text-xs">
            <Badge variant="outline">All Stock {num(product.available)}</Badge>
            <Badge variant="outline" className="text-amber-700 border-amber-300">Home {num(product.home)}</Badge>
            <Badge variant="outline" className="text-blue-700 border-blue-300">Store {num(product.store)}</Badge>
            <Badge variant="outline" className="text-status-passed border-green-300">Pre-Order {num(product.preorder)}</Badge>
          </div>
        </DialogHeader>
        <Tabs key={`${product.id}-${initialTab}`} defaultValue={initialTab}>
          <TabsList data-testid="pl-tabs">
            <TabsTrigger value="varian" data-testid="pl-tab-varian">Varian</TabsTrigger>
            <TabsTrigger value="gudang" data-testid="pl-tab-gudang">Stok per Gudang</TabsTrigger>
            <TabsTrigger value="movement" data-testid="pl-tab-movement">Stock Movement</TabsTrigger>
            <TabsTrigger value="hpp" data-testid="pl-tab-hpp">HPP</TabsTrigger>
            <TabsTrigger value="penjualan" data-testid="pl-tab-penjualan">Ringkasan Penjualan</TabsTrigger>
            <TabsTrigger value="kol" data-testid="pl-tab-kol">Riwayat KOL</TabsTrigger>
          </TabsList>

          <TabsContent value="varian">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Varian</TableHead><TableHead className="text-right">Harga Jual</TableHead>
                <TableHead className="text-right">All Stock</TableHead><TableHead className="text-right">Home</TableHead>
                <TableHead className="text-right">Store</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {vs.map((v) => (
                  <TableRow key={v.variant_id} data-testid={`pl-var-${v.variant_id}`}>
                    <TableCell className="font-medium text-cocoa">{v.color}/{v.size}</TableCell>
                    <TableCell className="text-right">{v.selling_price ? fmtIDR(v.selling_price) : "-"}</TableCell>
                    <TableCell className="text-right font-semibold">{num(v.available)}</TableCell>
                    <TableCell className="text-right text-amber-700">{num(v.home)}</TableCell>
                    <TableCell className="text-right text-blue-700">{num(v.store)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => onHistory(v)} className="text-taupe h-7 px-2" data-testid={`pl-var-history-${v.variant_id}`}><History className="w-3.5 h-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
                {!vs.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-6">Tidak ada varian.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="gudang">
            <WarehouseStockTab product={product} warehouses={warehouses} write={write} onSaved={onSaved} />
          </TabsContent>

          <TabsContent value="movement">
            <MovementTab variants={vs} />
          </TabsContent>

          <TabsContent value="hpp">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Varian</TableHead><TableHead className="text-right">HPP</TableHead>
                <TableHead className="text-right">Harga Jual</TableHead><TableHead className="text-right">Margin</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {vs.map((v) => {
                  const margin = v.selling_price && v.hpp ? Math.round(((v.selling_price - v.hpp) / v.selling_price) * 100) : null;
                  return (
                    <TableRow key={v.variant_id}>
                      <TableCell className="font-medium text-cocoa">{v.color}/{v.size}</TableCell>
                      <TableCell className="text-right">{v.hpp ? fmtIDR(v.hpp) : "-"}</TableCell>
                      <TableCell className="text-right">{v.selling_price ? fmtIDR(v.selling_price) : "-"}</TableCell>
                      <TableCell className="text-right">{margin == null ? "-" : `${margin}%`}</TableCell>
                    </TableRow>
                  );
                })}
                {!vs.length && <TableRow><TableCell colSpan={4} className="text-center text-wmuted py-6">Tidak ada varian.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="penjualan">
            <SalesSummaryTab productId={product.id} />
          </TabsContent>

          <TabsContent value="kol">
            <KolHistoryTab productId={product.id} />
          </TabsContent>
        </Tabs>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WarehouseStockTab({ product, warehouses, write, onSaved }) {
  const vs = product.variants || [];
  const [edits, setEdits] = useState({});
  const cur = (v, wId) => v.per_warehouse?.[wId]?.available || 0;
  const getVal = (vid, wId) => { const k = `${vid}:${wId}`; return edits[k] !== undefined ? edits[k] : String(cur(vs.find((x) => x.variant_id === vid), wId)); };
  const setVal = (vid, wId, val) => setEdits((e) => ({ ...e, [`${vid}:${wId}`]: val }));

  const saveVariant = async (v) => {
    try {
      let saved = 0;
      for (const w of warehouses) {
        const k = `${v.variant_id}:${w.id}`;
        if (edits[k] === undefined) continue;
        const qty = parseInt(edits[k]) || 0;
        if (qty === cur(v, w.id)) continue;
        const response = await api.post("/stock/set", {
          variant_id: v.variant_id,
          warehouse_id: w.id,
          qty,
        });
        if (Number(response.data?.qty) !== qty) {
          throw new Error("Stok tersimpan tidak sesuai angka yang dimasukkan");
        }
        saved++;
      }
      if (saved) {
        toast.success("Stok Available per gudang sudah diperbarui");
        onSaved();
      }
      else toast.info("Tidak ada perubahan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="space-y-3 md:hidden" data-testid="pl-mobile-warehouse-stock-tab">
        <p className="text-xs text-wmuted">
          Angka adalah stok Available (siap jual) per gudang.
        </p>
        {vs.map((variant) => (
          <div
            key={variant.variant_id}
            className="border border-taupe/20 rounded-lg p-3"
            data-testid={`pl-mobile-stock-row-${variant.variant_id}`}
          >
            <div className="font-medium text-cocoa">
              {variant.color}/{variant.size}
            </div>
            <div className="grid grid-cols-1 gap-2 mt-3">
              {warehouses.map((warehouse) => (
                <div key={warehouse.id}>
                  <Label className="text-xs text-wmuted">{warehouse.name}</Label>
                  {write ? (
                    <Input
                      type="number"
                      min="0"
                      value={getVal(variant.variant_id, warehouse.id)}
                      onChange={(e) => setVal(variant.variant_id, warehouse.id, e.target.value)}
                      className="h-9 mt-1 text-right"
                      data-testid={`pl-mobile-stock-input-${variant.variant_id}-${warehouse.id}`}
                    />
                  ) : (
                    <div className="font-semibold text-cocoa mt-1">
                      {num(cur(variant, warehouse.id))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {write && (
              <div className="flex justify-end mt-3">
                <Button
                  size="sm"
                  onClick={() => saveVariant(variant)}
                  className="bg-cocoa hover:bg-cocoa-dark text-ivory"
                  data-testid={`pl-mobile-stock-save-${variant.variant_id}`}
                >
                  <Save className="w-3.5 h-3.5 mr-1" />
                  Update Stok
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="hidden overflow-x-auto md:block">
      <p className="text-xs text-wmuted mb-2">Angka = stok <b>Available (siap jual)</b> per gudang. On Hand = Available + Reserved + Allocated.</p>
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead>Varian</TableHead>
          {warehouses.map((w) => <TableHead key={w.id} className="text-right whitespace-nowrap">{w.name}</TableHead>)}
          {write && <TableHead className="text-right">Aksi</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {vs.map((v) => (
            <TableRow key={v.variant_id} className="bg-white">
              <TableCell className="text-sm font-medium text-cocoa">{v.color}/{v.size}</TableCell>
              {warehouses.map((w) => (
                <TableCell key={w.id} className="text-right">
                  {write
                    ? <Input
                        type="number"
                        min="0"
                        value={getVal(v.variant_id, w.id)}
                        onChange={(e) => setVal(v.variant_id, w.id, e.target.value)}
                        className="w-20 h-8 text-right ml-auto"
                        data-testid={`pl-stock-input-${v.variant_id}-${w.id}`}
                      />
                    : num(cur(v, w.id))}
                </TableCell>
              ))}
              {write && <TableCell className="text-right"><Button size="sm" onClick={() => saveVariant(v)} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-7 px-2" data-testid={`pl-stock-save-${v.variant_id}`}><Save className="w-3.5 h-3.5 mr-1" />Update Stok</Button></TableCell>}
            </TableRow>
          ))}
          {!vs.length && <TableRow><TableCell colSpan={1 + warehouses.length + (write ? 1 : 0)} className="text-center text-wmuted py-6">Tidak ada varian.</TableCell></TableRow>}
        </TableBody>
      </Table>
      </div>
    </>
  );
}

function MovementTab({ variants }) {  const [vid, setVid] = useState(variants[0]?.variant_id || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!vid) return;
    setLoading(true);
    api.get("/stock/movements", { params: { variant_id: vid } }).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [vid]);
  return (
    <div>
      <div className="mb-3 w-full sm:w-72">
        <Label>Varian</Label>
        <Select value={vid} onValueChange={setVid}>
          <SelectTrigger data-testid="pl-mv-variant"><SelectValue placeholder="Pilih varian" /></SelectTrigger>
          <SelectContent>{variants.map((v) => <SelectItem key={v.variant_id} value={v.variant_id}>{v.color}/{v.size}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto max-h-[45vh] overflow-y-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Tanggal</TableHead><TableHead>Tipe</TableHead><TableHead>Ref</TableHead>
            <TableHead>Bucket</TableHead><TableHead className="text-right">Before</TableHead>
            <TableHead className="text-right">Δ</TableHead><TableHead className="text-right">After</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-6">Memuat...</TableCell></TableRow>}
            {!loading && rows.map((m, i) => (
              <TableRow key={m.id || i} data-testid={`pl-mv-${m.id || i}`}>
                <TableCell className="text-xs whitespace-nowrap">{(m.datetime || "").slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell className="text-xs">{m.movement_type}</TableCell>
                <TableCell className="text-xs">{m.reference || "-"}</TableCell>
                <TableCell className="text-xs">{m.qc_status}</TableCell>
                <TableCell className="text-right">{num(m.qty_before)}</TableCell>
                <TableCell className={`text-right font-medium ${m.qty_change >= 0 ? "text-status-passed" : "text-status-reject"}`}>{m.qty_change >= 0 ? "+" : ""}{num(m.qty_change)}</TableCell>
                <TableCell className="text-right font-semibold">{num(m.qty_after)}</TableCell>
              </TableRow>
            ))}
            {!loading && !rows.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-6">Belum ada pergerakan stok.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SalesSummaryTab({ productId }) {
  const [avail, setAvail] = useState(null);
  const [start, setStart] = useState(monthStartStr());
  const [end, setEnd] = useState(todayStr());
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(`/preorders/product-summary/${productId}`).then((r) => setAvail(r.data)).catch(() => setAvail(null)); }, [productId]);
  const loadSales = () => {
    setLoading(true);
    api.get(`/products/${productId}/sales-summary`, { params: { start: start || undefined, end: end || undefined } })
      .then((r) => setSales(r.data)).catch(() => setSales(null)).finally(() => setLoading(false));
  };
  useEffect(() => { loadSales(); /* eslint-disable-next-line */ }, [productId]);
  const po = avail?.preorder || {};
  const Card = ({ title, children, testid }) => (
    <div className="rounded-lg border border-taupe/25 bg-white p-4" data-testid={testid}>
      <div className="text-xs font-semibold text-wmuted uppercase tracking-wide mb-2">{title}</div>
      {children}
    </div>
  );
  const NotConfigured = () => <div className="text-sm text-amber-700 font-medium">Belum dikonfigurasi</div>;
  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="pl-ss-start" className="w-40" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="pl-ss-end" className="w-40" /></div>
        <Button onClick={loadSales} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pl-ss-apply">Terapkan</Button>
      </div>
      {loading ? <div className="text-center text-wmuted py-6">Memuat...</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <Card title="Total Terjual" testid="pl-ss-qty"><div className="text-2xl font-bold text-cocoa">{num(sales?.total_qty)} <span className="text-sm font-normal text-wmuted">pcs</span></div></Card>
            <Card title="Total Pendapatan" testid="pl-ss-revenue"><div className="text-2xl font-bold text-status-passed">{fmtIDR(sales?.total_revenue || 0)}</div></Card>
            <Card title="Jumlah Transaksi" testid="pl-ss-orders"><div className="text-2xl font-bold text-cocoa">{num(sales?.orders)}</div></Card>
          </div>
          {sales?.per_variant?.length > 0 ? (
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Varian</TableHead><TableHead className="text-right">Terjual</TableHead><TableHead className="text-right">Pendapatan</TableHead></TableRow></TableHeader>
              <TableBody>
                {sales.per_variant.map((r, i) => (
                  <TableRow key={i}><TableCell className="text-cocoa">{r.color}/{r.size}</TableCell><TableCell className="text-right font-semibold">{num(r.qty)}</TableCell><TableCell className="text-right">{fmtIDR(r.revenue)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <div className="text-sm text-wmuted py-4 text-center">Tidak ada penjualan pada periode ini.</div>}
        </>
      )}
      <div className="border-t border-taupe/20 mt-4 pt-3">
        <p className="text-xs text-wmuted mb-2">Ketersediaan penjualan (read-only), terpisah dari stok fisik gudang.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card title="HOME Available" testid="pl-summary-home">{avail?.home?.configured ? <div className="text-xl font-bold text-cocoa">{num(avail.home.available)}</div> : <NotConfigured />}</Card>
          <Card title="STORE Available" testid="pl-summary-store">{avail?.store?.configured ? <div className="text-xl font-bold text-cocoa">{num(avail.store.available)}</div> : <NotConfigured />}</Card>
          <Card title="PRE_ORDER Remaining" testid="pl-summary-preorder">{po.configured ? <><div className="text-xl font-bold text-status-passed">{num(po.remaining)}</div><div className="text-xs text-wmuted mt-1">Planned {num(po.planned)} · Reserved {num(po.reserved)} · Sold {num(po.sold)}</div></> : <div className="text-sm text-wmuted">Belum ada kuota</div>}</Card>
        </div>
      </div>
    </div>
  );
}

function KolHistoryTab({ productId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/kol/product-history/${productId}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [productId]);
  if (loading) return <div className="text-center text-wmuted py-8">Memuat…</div>;
  const items = data?.items || [];
  return (
    <div data-testid="pl-kol-history">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg border border-taupe/25 bg-white p-3" data-testid="pl-kol-unique">
          <div className="text-xs font-semibold text-wmuted uppercase tracking-wide mb-1">Jumlah KOL</div>
          <div className="text-2xl font-bold text-cocoa">{num(data?.unique_kols)}</div>
        </div>
        <div className="rounded-lg border border-taupe/25 bg-white p-3" data-testid="pl-kol-qty">
          <div className="text-xs font-semibold text-wmuted uppercase tracking-wide mb-1">Produk Terkirim</div>
          <div className="text-2xl font-bold text-status-passed">{num(data?.total_qty_shipped)} <span className="text-sm font-normal text-wmuted">pcs</span></div>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead>KOL</TableHead><TableHead>No. Pengajuan</TableHead><TableHead>Varian</TableHead>
          <TableHead className="text-right">Qty</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={`${it.submission_id}-${it.variant_id}`} data-testid={`pl-kol-row-${it.submission_id}`}>
              <TableCell className="text-sm text-cocoa">{it.kol_name}<span className="text-xs text-wmuted ml-1">({it.kol_code})</span>{it.campaign && <div className="text-xs text-wmuted">{it.campaign}</div>}</TableCell>
              <TableCell className="font-mono text-xs">{it.number}</TableCell>
              <TableCell className="text-sm">{it.color}/{it.size}</TableCell>
              <TableCell className="text-right font-medium">{num(it.qty)}</TableCell>
              <TableCell className="text-xs">{it.status}</TableCell>
            </TableRow>
          ))}
          {!items.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">Produk ini belum pernah dikerjasamakan dengan KOL.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function EditProductDialog({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product.name || "", category: product.category || "", status: product.active === false ? "INACTIVE" : "ACTIVE",
    active: product.active !== false, selling_price: product.selling_price ?? "", purchase_price: product.purchase_price ?? "",
    description: product.description || "", is_favorite: !!product.is_favorite, sku: product.sku || "", main_photo: product.main_photo || null,
  });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    try {
      await api.put(`/products/${product.id}`, {
        name: form.name, category: form.category, status: form.status, active: form.active,
        sku: form.sku || null, main_photo: form.main_photo,
        selling_price: form.selling_price === "" ? null : Number(form.selling_price),
        purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
        description: form.description, is_favorite: form.is_favorite,
      });
      toast.success("Produk diperbarui"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pl-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit Produk</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pl-edit-name" /></div>
          <div><Label>Category</Label>
            <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="pl-edit-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v, active: v === "ACTIVE" })}>
              <SelectTrigger data-testid="pl-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="INACTIVE">Inactive</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Harga Beli</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></div>
          <div><Label>Harga Jual</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="pl-edit-save" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
